import { useEffect, useState } from "react";
import { Instagram, Copy, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const useSlides = (base, post) => {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const n = post?.slides?.length || 0;
    if (!n) { setUrls([]); return undefined; }
    let objs = [];
    Promise.all(Array.from({ length: n }, (_, i) => api.get(`${base}/instagram/${i + 1}.jpg`, { responseType: "blob" })))
      .then((rs) => { objs = rs.map((r) => URL.createObjectURL(r.data)); setUrls(objs); })
      .catch(() => setUrls([]));
    return () => objs.forEach((u) => URL.revokeObjectURL(u));
  }, [base, post?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return urls;
};

const save = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); };

export const CompareSocial = ({ c, reload, base = `/compare/${c.id}`, post: given, blurb, zipName }) => {
  const post = given === undefined ? c.social_post : given;
  const urls = useSlides(base, post);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (post) setText(`${post.caption}\n.\n.\n${post.hashtags.join(" ")}`); }, [post?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try { await api.post(`${base}/instagram`); toast.success("Instagram post ready"); await reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const zip = () => api.get(`${base}/instagram.zip`, { responseType: "blob" }).then((r) => save(r.data, zipName || `${(c.title || c.id).toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60)}-instagram.zip`)).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="border border-[#E2D9CC] bg-[#FAF7F2] p-7" data-testid="compare-social">
      <div className="flex items-center gap-3 mb-4">
        <Instagram className="w-5 h-5 text-[#C59B27]" strokeWidth={1.4} />
        <p className="eyebrow">Instagram post</p>
        {post && <span className="ml-auto text-[11px] font-mono-accent uppercase tracking-wider text-[#8C827A]" data-testid="compare-social-meta">{urls.length} slides · {post.hashtags.length} hashtags</span>}
      </div>
      {post ? (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
            {urls.map((u, i) => <img key={u} data-testid={`compare-slide-${i + 1}`} src={u} alt={`Slide ${i + 1}`} className="w-full aspect-[4/5] object-cover border border-[#E2D9CC]" />)}
          </div>
          <Textarea data-testid="compare-social-caption" rows={8} value={text} onChange={(e) => setText(e.target.value)} className="bg-white rounded-none text-sm mb-4" />
          <div className="flex flex-wrap gap-3">
            <Button data-testid="compare-social-copy" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Caption and hashtags copied"))} className="rounded-none"><Copy className="w-4 h-4 mr-2" />Copy caption + hashtags</Button>
            <Button data-testid="compare-social-zip" variant="outline" onClick={zip} className="rounded-none"><Download className="w-4 h-4 mr-2" />Download slides (ZIP)</Button>
            <Button data-testid="compare-social-regenerate" variant="ghost" onClick={create} disabled={busy} className="rounded-none"><RefreshCw className="w-4 h-4 mr-2" />{busy ? "Writing…" : "New version"}</Button>
          </div>
          <p className="text-xs text-[#8C827A] mt-3">Post the slides in the order shown. The ZIP includes caption.txt.</p>
        </>
      ) : (
        <>
          <p className="text-sm text-[#1C181D] mb-4">{blurb || "A swipe-through carousel: a cover, one slide per town with its scores, and our pick, plus a ready caption and hashtags."}</p>
          <Button data-testid="compare-social-create" onClick={create} disabled={busy} className="rounded-none"><Instagram className="w-4 h-4 mr-2" />{busy ? "Writing…" : "Create Instagram post"}</Button>
        </>
      )}
    </div>
  );
};
